import { Alert, Divider, Form, Input, Space, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { RegionDistrictIdFields, type GeoFallback } from '@/features/geo/RegionDistrictIdFields'
import { ContactRevealControl } from './ContactRevealControl'
import {
  ADDRESS_MAX,
  EMAIL_MAX,
  EMAIL_SHAPE,
  FULL_NAME_MAX,
  NOTE_MAX,
  PHONE_MAX,
  PHONE_MIN,
  PHONE_SAFE,
  normalizeContactPhone,
} from './contactApi'

interface ContactFieldsProps {
  /** Forma ichidagi guruh nomi — qiymatlar shu ostida yig'iladi */
  group: string
  loading?: boolean
  /** Tahrirlash: abonentda kontakt YO'Q (bo'sh maydonlar «o'chirilgan» emas) */
  missing?: boolean
  /**
   * Niqoblangan raqam hali ochilmagan — blok FAQAT O'QISH uchun.
   * Sabab {@link buildContactRequest} javadoc'ida.
   */
  locked?: boolean
  /** `contact.write` yo'q — ko'rsatiladi, lekin tahrirlanmaydi */
  readOnly?: boolean
  canReveal?: boolean
  onReveal?: (reason: string) => void
  revealing?: boolean
  revealError?: string | null
  fallbackRegion?: GeoFallback | null
  fallbackDistrict?: GeoFallback | null
}

/**
 * Abonent formasidagi kontakt bo'limi — `platform.contact` (ADR-031).
 *
 * ⚠️ Viloyat va tuman FAQAT TANLANADI (GEO §6): erkin matn «Toshkent» va
 * «toshkent» ni ikki hududga bo'lib yuborardi. Yangi viloyat `/geo/regions`
 * sahifasida ochiladi.
 *
 * ⚠️ Telefon FORMATI MAJBURLANMAYDI (GEO §7) — tizim Afg'onistonda ham
 * ishlaydi. Tekshiruv faqat serverdagi `contact_phone_shape` ning aynan
 * o'zi: uzunlik va xavfli belgi yo'qligi.
 */
export function ContactFields({
  group,
  loading = false,
  missing = false,
  locked = false,
  readOnly = false,
  canReveal = false,
  onReveal,
  revealing = false,
  revealError = null,
  fallbackRegion = null,
  fallbackDistrict = null,
}: ContactFieldsProps) {
  const { t } = useTranslation()

  const phonePath = [group, 'phone']

  const phoneShape = (value: string | undefined): Promise<void> => {
    const clean = normalizeContactPhone(value)
    if (clean === null) return Promise.resolve()
    if (clean.length < PHONE_MIN || clean.length > PHONE_MAX || !PHONE_SAFE.test(clean)) {
      return Promise.reject(new Error(t('contact.phoneShape', { min: PHONE_MIN, max: PHONE_MAX })))
    }
    return Promise.resolve()
  }

  return (
    <>
      <Typography.Text strong>{t('contact.section')}</Typography.Text>
      <Divider style={{ margin: '8px 0 16px' }} />

      {missing ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('contact.missingTitle')}
          description={t('contact.missingHint')}
        />
      ) : null}

      {locked ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('contact.maskedTitle')}
          description={
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <span>{canReveal ? t('contact.maskedHint') : t('contact.maskedNoPermission')}</span>
              {canReveal && onReveal ? (
                <ContactRevealControl
                  onReveal={onReveal}
                  revealing={revealing}
                  revealError={revealError}
                />
              ) : null}
            </Space>
          }
        />
      ) : null}

      <Form.Item name={[group, 'fullName']} label={t('contact.fullName')}>
        <Input autoComplete="off" disabled={locked || loading || readOnly} maxLength={FULL_NAME_MAX} />
      </Form.Item>

      <Form.Item
        name={phonePath}
        label={t('contact.phone')}
        extra={t('contact.phoneHint')}
        rules={[{ validator: (_, value: string | undefined) => phoneShape(value) }]}
      >
        <Input autoComplete="off" inputMode="tel" disabled={locked || loading || readOnly} />
      </Form.Item>

      <Form.Item
        name={[group, 'phoneAlt']}
        label={t('contact.phoneAlt')}
        // ⚠️ Asosiy raqam o'zgarganda QAYTA tekshiriladi: aks holda
        // «asosiysini o'chirdim, qo'shimchasi qoldi» holati formadan
        // o'tib ketardi va server uni 400 bilan rad etardi.
        dependencies={[phonePath]}
        rules={[
          ({ getFieldValue }) => ({
            validator: async (_, value: string | undefined) => {
              await phoneShape(value)
              const alt = normalizeContactPhone(value)
              if (alt === null) return
              const main = normalizeContactPhone(getFieldValue(phonePath) as string | undefined)
              // Serverdagi `contact_phone_alt_needs_phone` ning aksi: yolg'iz
              // ikkinchi raqam «raqamsiz kontakt» so'roviga tushib qolardi.
              if (main === null) throw new Error(t('contact.phoneAltNeedsPhone'))
              // Bir xil raqam ikki ustunda — bitta odamga ikki marta SMS
              if (main === alt) throw new Error(t('contact.phoneAltSame'))
            },
          }),
        ]}
      >
        <Input autoComplete="off" inputMode="tel" disabled={locked || loading || readOnly} />
      </Form.Item>

      <Form.Item
        name={[group, 'email']}
        label={t('contact.email')}
        rules={[
          { max: EMAIL_MAX, message: t('contact.emailLength', { max: EMAIL_MAX }) },
          { pattern: EMAIL_SHAPE, message: t('contact.emailShape') },
        ]}
      >
        <Input autoComplete="off" inputMode="email" disabled={locked || loading || readOnly} />
      </Form.Item>

      <RegionDistrictIdFields
        regionPath={[group, 'regionId']}
        districtPath={[group, 'districtId']}
        disabled={locked || loading || readOnly}
        fallbackRegion={fallbackRegion}
        fallbackDistrict={fallbackDistrict}
      />

      <Form.Item name={[group, 'address']} label={t('contact.address')}>
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 3 }}
          maxLength={ADDRESS_MAX}
          disabled={locked || loading || readOnly}
        />
      </Form.Item>

      <Form.Item name={[group, 'note']} label={t('contact.note')}>
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 3 }}
          maxLength={NOTE_MAX}
          disabled={locked || loading || readOnly}
        />
      </Form.Item>
    </>
  )
}
